import React, { ReactNode, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { ChevronRight } from 'react-feather';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { CellContext } from '@tanstack/table-core';

import { ArrowUp, ArrowDown, CaretRight, CaretDown, CornerDownRight } from '~/ui/assets/icons';
import { Popover, Shimmer } from '~/ui/components';
import useScreenSize from '~/hooks/useScreenSize';
import {
  Tbody,
  Thead,
  TableContainer,
  NotFoundMessage,
  Cell,
  AlignType,
  TableWrapper,
  ToggleButton,
  ResponsiveWrapper,
  RespCol,
  RespHeader,
  RespValue,
  Wrapper,
  Dot,
  DotContent,
  GroupedCell,
} from './Table.styled';

type CustomColumnDef<T> = ColumnDef<any[] & T> & {
  cellAlign?: AlignType;
  headerAlign?: AlignType;
  ellipsis?: boolean;
};
export type TableColumns<T> = CustomColumnDef<T>[];
export type CellProps<T> = CellContext<any[] & T, unknown>;

interface TableProps {
  data?: any[];
  loading?: boolean;
  columns?: CustomColumnDef<any>[];
  rowHeight?: number;
  noItemsMessage?: string | ReactNode;
  sortable?: boolean;
  initialSorting?: SortingState;
  withoutBorders?: boolean;
  isTHeadFixed?: boolean;
  noPadding?: boolean;
  maxHeight?: number | string;
  shimmerLines?: number;
  responsiveCols?: string[];
  responsiveWidth?: number;
  onRowClick?: (row: any) => void;
  testId?: string;
  renderExpandedRow?: (row: any) => ReactNode;
  timeline?: boolean;
  getRowProps?: (row: any, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  zeroMargin?: boolean;
  manualSorting?: boolean;
  onChangeSorting?: (sorting: string) => void;
  grouping?: string[];
}

export function Table({
  data,
  loading,
  columns,
  rowHeight,
  noItemsMessage,
  sortable,
  initialSorting,
  withoutBorders = false,
  isTHeadFixed = false,
  maxHeight,
  shimmerLines = 3,
  responsiveCols = columns?.slice(0, 4).map((el: any) => el.accessorKey || el.id),
  responsiveWidth = 1280,
  testId = 'ui',
  onRowClick,
  renderExpandedRow,
  timeline = false,
  getRowProps,
  zeroMargin = false,
  manualSorting = false,
  onChangeSorting,
  grouping = [],
  ...props
}: TableProps) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting || []);
  const hasSubRows = useMemo(() => data?.some((row) => row?.children?.length || row?.canExpand), [data]);
  const responsiveView = responsiveWidth ? useScreenSize(`max-width: ${responsiveWidth}px`) : null;
  const { colors } = useTheme();

  const handleSortingChange = useCallback(
    (sortingState: any) => {
      const sortingArray = sortingState?.() || [];
      setSorting(sortingState);
      const sortBy = sortingArray?.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`)?.join(',');
      onChangeSorting?.(sortBy);
    },
    [sorting],
  );

  const getResponsiveColumns = (responsiveCols, columns) => {
    const visibleCols = columns.filter(
      (col: any) => responsiveCols.includes(col.id) || responsiveCols.includes(col.accessorKey),
    );

    const respCols = columns.filter(
      (el) => !visibleCols.map((el) => el.accessorKey || el.id).includes(el.accessorKey || el.id),
    );

    return {
      respCols,
      visibleCols,
    };
  };

  const renderResponsiveRowCell = (context: any) => {
    const { respCols } = getResponsiveColumns(responsiveCols, columns);

    const renderCell = (col, ctx) => {
      if (col?.accessorFn) {
        if (typeof col?.accessorFn === 'function') {
          return col?.accessorFn?.(ctx.row.original);
        }

        return col?.accessorFn;
      }

      if (typeof col?.cell === 'function') {
        return col?.cell?.({
          ...ctx,
          getValue: () => ctx.row.original[col.accessorKey || col.id],
        });
      }

      return col?.cell;
    };

    if (context.row.getIsGrouped()) {
      return <></>;
    }

    return (
      <Popover
        noHeader
        placement="bottomRight"
        trigger="click"
        width="auto"
        index={10}
        content={
          <ResponsiveWrapper>
            {respCols.map((c) => {
              const col = c as any;
              return (
                <RespCol key={col.accessorKey || col.id}>
                  <RespHeader>{typeof col?.header === 'function' ? col?.header?.(context) : col?.header}</RespHeader>
                  <RespValue>{renderCell(col, context)}</RespValue>
                </RespCol>
              );
            })}
          </ResponsiveWrapper>
        }
      >
        <ChevronRight color={colors.brand.primary} />
      </Popover>
    );
  };

  const columnsView = useMemo(() => {
    const { visibleCols } = getResponsiveColumns(responsiveCols, columns);

    if (responsiveView && responsiveView <= responsiveWidth && columns.length > responsiveCols.length) {
      return [
        ...visibleCols,
        {
          accessorKey: 'responsive-resume',
          header: () => '',
          cellAlign: 'right',
          cell: renderResponsiveRowCell,
        },
      ];
    }

    return columns;
  }, [responsiveView, columns, responsiveCols]);

  const renderSubRowCell = ({ row }: any) => {
    if (row.getCanExpand()) {
      return (
        <ToggleButton
          {...{
            onClick: row.getToggleExpandedHandler(),
          }}
        >
          {row.getIsExpanded() ? <CaretDown /> : <CaretRight />}
        </ToggleButton>
      );
    }

    if (row.depth > 0) {
      return <CornerDownRight className="depth-icon" />;
    }

    return <></>;
  };

  const getSubRowsColumn = (cols): TableColumns<any> => {
    if (!hasSubRows) return cols;
    return [
      {
        accessorKey: 'sub-rows',
        header: () => '',
        cellAlign: 'right',
        cell: renderSubRowCell,
      },
      ...cols,
    ];
  };

  const renderGroupedCell = (cell: any, row: any) => (
    <GroupedCell onClick={row.getToggleExpandedHandler()}>
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </GroupedCell>
  );

  const renderCell = (cell: any) => {
    if (cell.getIsAggregated() && cell.column.columnDef.enableGrouping === false) {
      return <></>;
    }

    return (
      <Cell
        minSize={cell.column.columnDef.minSize}
        maxSize={cell.column.columnDef.maxSize}
        align={(cell.column.columnDef as any).cellAlign}
        ellipsis={(cell.column.columnDef as any).ellipsis}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Cell>
    );
  };

  const state: any = { sorting };

  if (grouping?.length) {
    state.expanded = true;
  }

  const table = useReactTable({
    data,
    columns: getSubRowsColumn(columnsView),
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: handleSortingChange,
    getSubRows: (row) => row.children,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getRowCanExpand: (row) => !!grouping?.length || !!row.original.canExpand || !!row.original.children?.length,
    state,
    enableSorting: sortable,
    enableSortingRemoval: false,
    initialState: { sorting, grouping },
  });

  if (!loading && !data?.length) {
    return <NotFoundMessage>{noItemsMessage || 'Nenhum item encontrado'}</NotFoundMessage>;
  }

  return (
    <Wrapper className="wrapper">
      <TableWrapper $maxHeight={maxHeight} hasSubRows={hasSubRows} zeroMargin={zeroMargin} {...props}>
        <TableContainer loading={loading ? 1 : 0}>
          <Thead $isTHeadFixed={isTHeadFixed}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`th-${header.id} ${header.column.getCanSort() ? 'cursor-pointer' : ''}`}
                    style={{ textAlign: (header.column.columnDef as any).headerAlign }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: (header.column.columnDef as any).headerAlign,
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

                      {{
                        asc: <ArrowUp className="arrow" />,
                        desc: <ArrowDown className="arrow" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </Thead>

          {loading && (
            <Tbody rowHeight={rowHeight} $withoutBorders={withoutBorders}>
              {table.getHeaderGroups().map((headerGroup) =>
                Array.from({ length: shimmerLines }).map((_, i) => (
                  <tr key={`shimmer_${headerGroup.id}_${i}`}>
                    {headerGroup.headers.map((header) => (
                      <td key={`shimmer_td_${header.id}`}>
                        <Shimmer width={1000} height={25} />
                      </td>
                    ))}
                  </tr>
                )),
              )}
            </Tbody>
          )}

          {!loading && data?.length && (
            <Tbody
              rowHeight={rowHeight}
              $withoutBorders={withoutBorders}
              $timeline={timeline}
              sclickable={!!onRowClick}
            >
              {table.getRowModel()?.rows?.map((row, index) => {
                const additionalRowProps = getRowProps?.(row, index) || {};
                const { className: rowClassNameProp, onClick: rowOnClick, ...restRowProps } = additionalRowProps;
                const groupedClass = row.getIsGrouped() ? 'grouped' : '';
                const mergedClassName = [row.depth > 0 && `sub-row`, rowClassNameProp, groupedClass]
                  .filter((el) => el)
                  .join(' ');

                const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
                  rowOnClick?.(event);
                  if (!event.defaultPrevented) {
                    onRowClick?.(row.original);
                  }
                };

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={mergedClassName}
                      onClick={handleRowClick}
                      data-testid={`${testId}-table-row-${index}`}
                      {...restRowProps}
                    >
                      {row.getVisibleCells().map((cell, i) => (
                        <td
                          key={cell.id}
                          className={[`td-${cell.column.id}`, i === 0 && 'first'].filter((el) => el).join(' ')}
                          data-testid={`${testId}-table-row-${index}-cell-${i}`}
                        >
                          {cell.getIsGrouped()
                            ? renderGroupedCell(cell, row) // If it's a grouped cell, add an expander and row count
                            : renderCell(cell)}
                        </td>
                      ))}
                      {timeline && (
                        <DotContent>
                          <Dot />
                        </DotContent>
                      )}
                    </tr>
                    {row.getIsExpanded() && renderExpandedRow && (
                      <tr>
                        <td colSpan={columns.length + 1}>{renderExpandedRow(row.original)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          )}
        </TableContainer>
      </TableWrapper>
    </Wrapper>
  );
}
